import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useApiUrl } from './ApiContext';

function SeedMix() {
  const initialSeeds = [
    { variety: 'Barley (Spring)' },
    { variety: 'Buckwheat Essculentum' },
    { variety: 'Buckwheat Tataricum' },
    { variety: 'Canary Grass' },
    { variety: 'Chicory' },
    { variety: 'Clover Berseem' },
    { variety: 'Clover Berseem TABOR' },
    { variety: 'Clover Crimson' },
    { variety: 'Clover Red' },
    { variety: 'Clover White' },
    { variety: 'Fenugreek' },
    { variety: 'Forage Rape' },
    { variety: 'Gold of Pleasure (Camelina)' },
    { variety: 'Kale Carbon (Ethiopian Kale)' },
    { variety: 'Kale Golden Eye' },
    { variety: 'Kale Interval (Kale/Rape)' },
    { variety: 'Kale Proteor' },
    { variety: 'Kale Thousand Head' },
    { variety: 'Linseed Spring' },
    { variety: 'Millet Japanese Reed' },
    { variety: 'Millet Red' },
    { variety: 'Millet White' },
    { variety: 'Mustard Brown' },
    { variety: 'Mustard White' },
    { variety: 'Oats (Spring)' },
    { variety: 'Oats Black' },
    { variety: 'Peas (Spring)' },
    { variety: 'Phacelia' },
    { variety: 'Quinoa' },
    { variety: 'Radish Class 1' },
    { variety: 'Radish Class 2' },
    { variety: 'Radish Oil' },
    { variety: 'Radish Tilage' },
    { variety: 'Reed Canary Grass' },
    { variety: 'Rye (Winter)' },
    { variety: 'Sorghum Dwarf' },
    { variety: 'Sorghum Intermediate' },
    { variety: 'Stubble Turnip' },
    { variety: 'Sunflower Gamecover' },
    { variety: 'Triticale (Spring)' },
    { variety: 'Vetch (Hairy)' },
    { variety: 'Vetch (Spring)' },
    { variety: 'Westerwold' },
    { variety: 'Wheat (Spring)' },
  ];

  const apiUrl = useApiUrl();

  // Top-level flow / UI state
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  // Core mixing state
  const [seeds, setSeeds] = useState([]);
  const [availableSeeds, setAvailableSeeds] = useState(initialSeeds);
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [selectedSeed, setSelectedSeed] = useState('');
  const [mixKg, setMixKg] = useState('');
  const kgNumber = Number(mixKg);
  const kgValid = Number.isFinite(kgNumber) && kgNumber >= 100;

  // Contact / comments
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [address, setAddress] = useState({ line1: '', line2: '', city: '', postcode: '' });
  const [telephone, setTelephone] = useState('');
  const [comments, setComments] = useState('');

  // Modal + step handling
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState('summary'); // 'summary' | 'details'
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);

  const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2})$/i;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailTouched(true);
    setEmailValid(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value));
    if (showModal) setShowModal(false);
  };

  const handleSeedSelection = (seedVariety) => {
    const selected = availableSeeds.find((s) => s.variety === seedVariety);
    if (selected) {
      setSeeds((prev) => [...prev, { ...selected, percentage: 0 }]);
      setAvailableSeeds((prev) => prev.filter((s) => s.variety !== seedVariety));
      setSelectedSeed('');
    }
  };

  const updateSeedPercentage = (index, value) => {
    let numValue = Math.round(Number(value));
    if (isNaN(numValue)) numValue = 0;
    if (numValue < 0) numValue = 0;
    if (numValue > 100) numValue = 100;

    const otherTotal = seeds.reduce((acc, seed, idx) => (idx !== index ? acc + seed.percentage : acc), 0);
    const remaining = 100 - otherTotal;
    if (numValue > remaining) numValue = remaining;

    const updated = seeds.map((seed, idx) =>
      idx === index ? { ...seed, percentage: numValue } : seed
    );
    setSeeds(updated);
    setTotalPercentage(updated.reduce((acc, s) => acc + s.percentage, 0));
    setShowModal(false);
  };

  const handleDelete = (index) => {
    const deleted = seeds[index];
    setAvailableSeeds((prev) => [...prev, { variety: deleted.variety }]);
    const updated = seeds.filter((_, idx) => idx !== index);
    setSeeds(updated);
    setTotalPercentage(updated.reduce((acc, s) => acc + s.percentage, 0));
    setShowModal(false);
  };

  const handleEnquire = () => {
    const cleaned = seeds.filter((s) => s.percentage > 0);
    setSeeds(cleaned);
    const newTotal = cleaned.reduce((acc, s) => acc + s.percentage, 0);
    setTotalPercentage(newTotal);
    if (newTotal === 100) {
      setModalStep('summary');
      setShowModal(true);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleCommentsChange = (e) => setComments(e.target.value);

  const summaryRows = useMemo(() => {
    const rows = seeds.filter((s) => s.percentage > 0);
    return rows.map((s) => ({
      variety: s.variety,
      percentage: s.percentage,
      kg: kgValid ? Number(((kgNumber * s.percentage) / 100).toFixed(2)) : null,
    }));
  }, [seeds, kgNumber, kgValid]);

  const totalKgFromRows = useMemo(() => {
    if (!kgValid) return 0;
    return summaryRows.reduce((sum, r) => sum + (r.kg || 0), 0);
  }, [summaryRows, kgValid]);

  const isFormValid = emailValid && totalPercentage === 100 && kgValid;

  const isAddressValid = () => {
    const { line1, city, postcode } = address;
    return (
      line1.trim() !== '' &&
      city.trim() !== '' &&
      ukPostcodeRegex.test(postcode.trim()) &&
      telephone.trim() !== ''
    );
  };

  const canSubmitDetails = isAddressValid() && privacyAccepted && !submitting;

  const handleSubmitEnquiry = async () => {
    try {
      setSubmitting(true);
      const breakdown = seeds
        .filter((s) => s.percentage > 0)
        .map((s) => ({
          variety: s.variety,
          percentage: s.percentage,
          kg: Number(((Number(mixKg) * s.percentage) / 100).toFixed(2)),
        }));

      const payload = {
        orderType: 'SeedMix',
        email,
        address: { ...address, telephone },
        comments,
        basket: {
          amountKg: Number(mixKg),
          seeds: breakdown,
        },
      };

      const { data } = await axios.post(`${apiUrl}/send-checkout-email`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
      });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to submit enquiry');
      }

      setShowModal(false);
      setEnquirySubmitted(true);
    } catch (err) {
      console.error('Seed Mix enquiry error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSeeds([]);
    setAvailableSeeds(initialSeeds);
    setTotalPercentage(0);
    setEmail('');
    setEmailTouched(false);
    setEmailValid(false);
    setAddress({ line1: '', line2: '', city: '', postcode: '' });
    setComments('');
    setMixKg('');
    setTelephone('');
    setEnquirySubmitted(false);
    setPrivacyAccepted(false);
    setShowModal(false);
    setModalStep('summary');
  };

  return (
    <div className="seed-mix-container">
      <div className="seed-mixer">
        {!disclaimerAccepted ? (
          <div className="disclaimer-screen">
            <h2>Before You Begin</h2>
            <p>
              We offer a wide range of standard cover crop solutions, which may already meet your needs.
              The pricing and enquiry process here applies to <strong>custom mixes only</strong>, beyond our regular range.
              Please check our existing product options before building a bespoke mix.
            </p>
            <div className="disclaimer-buttons">
              <div className="checkbox-and-button">
                <label className="disclaimer-checkbox">
                  <input
                    type="checkbox"
                    checked={disclaimerChecked}
                    onChange={(e) => setDisclaimerChecked(e.target.checked)}
                  />
                  I have read and understand
                </label>
                <button
                  onClick={() => setDisclaimerAccepted(true)}
                  className="start-anyway-button"
                  disabled={!disclaimerChecked}
                >
                  Create Custom Mix
                </button>
                <button
                  onClick={() =>
                    (window.location.href =
                      '/products?parent=Environmental&child=SFI&sub=Cover%20Crops')
                  }
                  className="go-to-products-button"
                >
                  View Standard Mixes
                </button>
              </div>
            </div>
          </div>
        ) : !enquirySubmitted ? (
          <>
            <h1 className="seed-mix-header">Cover Crop Seed Mixer</h1>

            {/* Email gate to proceed */}
            <div className="input-group">
              <input
                type="email"
                placeholder="To begin, enter your Email Address here."
                value={email}
                onChange={handleEmailChange}
                className={`input ${emailValid && emailTouched ? 'valid' : emailTouched ? 'invalid' : ''}`}
              />
            </div>

            {emailValid && (
              <>
                {/* Seeds list */}
                {seeds.map((seed, index) => (
                  <div key={index} className="seed-row">
                    <div className="seed-label">{seed.variety}</div>

                    <div className="seedmixrow-rest">
                      <div className="seedmix-row-item">
                        <div className="seedmix-row-title">Percentage of Mix</div>
                        <div className="seedmix-qtyctrls">
                          <button
                            className="seedmix-decrease"
                            type="button"
                            onClick={() => updateSeedPercentage(index, seed.percentage - 1)}
                            aria-label="Decrease percentage"
                            title="Decrease percentage"
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                          <input
                            className="seedmix-qty"
                            type="number"
                            value={seed.percentage}
                            onChange={(e) => updateSeedPercentage(index, e.target.value)}
                            min="0"
                            max="100"
                            step="1"
                            aria-label="Percentage"
                          />
                          <button
                            className="seedmix-increase"
                            type="button"
                            onClick={() => updateSeedPercentage(index, seed.percentage + 1)}
                            aria-label="Increase percentage"
                            title="Increase percentage"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                        </div>
                      </div>

                      <div className="seedmix-row-item">
                        <button
                          className="seedmix-delete"
                          type="button"
                          onClick={() => handleDelete(index)}
                          aria-label="Remove seed"
                          title="Remove seed"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add new seed when total < 100% */}
                {totalPercentage < 100 && (
                  <div className="input-group">
                    <select
                      value={selectedSeed}
                      onChange={(e) => {
                        setSelectedSeed(e.target.value);
                        handleSeedSelection(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        Choose a seed
                      </option>
                      {availableSeeds.map((seed, index) => (
                        <option key={index} value={seed.variety}>
                          {seed.variety}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <p className={`total-cost ${totalPercentage !== 100 ? 'invalid' : ''}`}>
                  Total Mix Percentage: ({totalPercentage}%)
                </p>

                {/* Amount (kg) */}
                <div className="amount-group">
                  <label htmlFor="mixKg">Amount</label>
                  <div className="amount-input-wrapper">
                    <input
                      id="mixKg"
                      type="number"
                      min="100"
                      step="1"
                      inputMode="numeric"
                      pattern="\d*"
                      placeholder="100"
                      value={mixKg}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') return setMixKg('');
                        if (/^\d+$/.test(val)) setMixKg(val);
                      }}
                      className={mixKg === '' ? '' : kgValid ? 'valid' : 'invalid'}
                      aria-invalid={mixKg !== '' && !kgValid}
                      aria-describedby="amountHelp"
                    />
                    <span className="amount-unit">KG</span>
                  </div>
                  {mixKg !== '' && !kgValid && (
                    <small id="amountHelp" className="validation-text">
                      Minimum order is 100 kg.
                    </small>
                  )}
                </div>

                <button type="button" disabled={!isFormValid} onClick={handleEnquire} className="submit-button">
                  Enquire
                </button>
              </>
            )}

            {/* Modal */}
            {showModal && (
              <div className="seedmix-modal-backdrop" onClick={handleModalClose}>
                <div className="seedmix-modal-content" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="seedmix-close-modal"
                    aria-label="Close"
                    title="Close"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>

                  {modalStep === 'summary' ? (
                    <>
                      <h2>Your Seed Mix Summary</h2>

                      <div className="seedmix-table-container">
                        <table className="seedmix-summary-table" role="table">
                          <thead>
                            <tr>
                              <th scope="col">Seed</th>
                              <th scope="col" className="col-perc">%</th>
                              <th scope="col" className="col-kg">KG</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryRows.map((row, i) => (
                              <tr key={i}>
                                <td className="cell-seed">{row.variety}</td>
                                <td className="cell-num">{row.percentage}</td>
                                <td className="cell-num">{row.kg?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="cell-total">Total</td>
                              <td className="cell-num">{totalPercentage}</td>
                              <td className="cell-num">{kgValid ? totalKgFromRows.toFixed(2) : '—'}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <div className="seedmix-actions single">
                        <button
                          type="button"
                          className="seedmix-toggle-btn"
                          onClick={() => setModalStep('details')}
                        >
                          Confirm & Proceed
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2>Enter Your Details</h2>

                      {/* Email shown read-only (they entered earlier) */}
                      <div className="input-group">
                        <input type="email" value={email} readOnly aria-label="Email Address" title="Email Address" />
                      </div>

                      <div className="input-group">
                        <input
                          type="text"
                          placeholder="Address Line 1"
                          name="line1"
                          value={address.line1}
                          onChange={handleAddressChange}
                          className={address.line1.trim() !== '' ? 'valid' : ''}
                        />
                      </div>

                      <div className="input-group">
                        <input
                          type="text"
                          placeholder="Address Line 2 (Optional)"
                          name="line2"
                          value={address.line2}
                          onChange={handleAddressChange}
                        />
                      </div>

                      <div className="input-group">
                        <input
                          type="text"
                          placeholder="City"
                          name="city"
                          value={address.city}
                          onChange={handleAddressChange}
                          className={address.city.trim() !== '' ? 'valid' : ''}
                        />
                      </div>

                      <div className="input-group">
                        <input
                          type="text"
                          placeholder="Postcode"
                          name="postcode"
                          value={address.postcode}
                          onChange={handleAddressChange}
                          className={ukPostcodeRegex.test(address.postcode.trim()) ? 'valid' : ''}
                        />
                      </div>

                      <div className="input-group">
                        <input
                          type="tel"
                          placeholder="Telephone"
                          value={telephone}
                          onChange={(e) => setTelephone(e.target.value)}
                          className={telephone.trim() !== '' ? 'valid' : ''}
                        />
                      </div>

                      <div className="input-group">
                        <textarea
                          placeholder="Add comments to your enquiry"
                          value={comments}
                          onChange={handleCommentsChange}
                        />
                      </div>

                      <label className="privacy-check">
                        <input
                          type="checkbox"
                          checked={privacyAccepted}
                          onChange={(e) => setPrivacyAccepted(e.target.checked)}
                        />
                        <span>
                          I have read and understand the{' '}
                          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                          </Link>
                          .
                        </span>
                      </label>

                      <div className="seedmix-actions">
                        <button
                          type="button"
                          className="seedmix-back-btn"
                          onClick={() => setModalStep('summary')}
                        >
                          Back to Summary
                        </button>

                        <button
                          type="button"
                          className="seedmix-confirm-btn"
                          disabled={!canSubmitDetails}
                          onClick={handleSubmitEnquiry}
                        >
                          {submitting ? 'Submitting...' : 'Submit Enquiry'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="enquiry-confirmation">
            <h1>Thank you for your enquiry</h1>
            <p>
              Thank you for submitting your custom cover crop seed mix enquiry.<br />
              A confirmation email has been sent with the details you provided.<br />
              If you do not see the email within a few minutes, please check your junk or spam folder.
            </p>
            <button type="button" className="start-new-enquiry-button" onClick={resetForm}>
              Start New Enquiry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SeedMix;
